import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';

import DocumentList from '../../presentation/features/documents/DocumentsList/DocumentsLists';
import LieferanspruchWidget from '../../presentation/features/lieferanspruch/LieferanspruchWidget/LieferanspruchWidget';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  DocumentFolder,
  DocumentSnapshotDto,
} from '../../services/cropConnectApiClient';
import {
  DetailViewWrapper,
  LeftPlaceholder,
  MainContentWrapper,
  RightWidgetContainer,
  StyledActionButtonContainer,
} from './styles';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import { useContextStore } from '../../stores/storeRoot';
import LieferanspruchModal from '../../presentation/features/lieferanspruch/LieferanspruchModal/LieferanspruchModal';
import { useI18n } from '../../hooks/useI18n';
import DocumentsWidget from '../../presentation/features/documents/DocumentsWidget/DocumentsWidget';
import { useDocumentsStore } from '../../stores/documentsStore';
import ActionButton from '../../presentation/components/ActionButton/ActionButton';
import PageHeader from '../../presentation/layout/PageHeader/PageHeader';
import { MainContainer } from '../../styles/styles';
import ContractReport from '../../presentation/reports/ContractReport/ContractReport';
import ContractFullfilmentReport from '../../presentation/reports/ContractFullfilmentReport/ContractFullfilmentReport';
import SecurityTrim, {
  securityTrimConditionsMet,
} from '../../services/auth/SecurityTrim';
import {
  contractFullfilmentReportRequiredRoleDefinitions,
  contractsReportRequiredRoleDefinitions,
} from '../../services/auth/predefinedPermissions/predefinedRoleDefinitionsGroups';
import { mapLocaleAccordingToNzRules } from '../../helpers/localeFormatter';
import {
  eContractingPath,
  individualDocumentsAnbauempfehlungPath,
} from '../../routes';
import GeneralDocumentsComponent from '../../presentation/features/general_documents/GeneralDocumentsComponent/GeneralDocumentsComponent';

interface DeliveryEntitlements {
  deliveryEntitlementTotal: string | number;
  contractedAmount: string | number;
  availableAmount: string | number;
  factorNzHolding: string | number;
  factorUnion: string | number;
}

const mapToDocument = (doc: DocumentSnapshotDto) => ({
  name: doc.name || '',
  url: '',
  id: doc.id || '',
});

const MyContracts: React.FC = () => {
  const {
    globalAssignment,
    businessPartnerAssignment,
    activeBpNumber,
    activeBpNumberCountry,
    nordzuckerInternalUser,
  } = useContextStore();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { getDocumentsPage, getDocumentsPageWithPrefilter } =
    useDocumentsStore();
  const [recentDocuments, setRecentDocuments] = useState<DocumentSnapshotDto[]>(
    []
  );
  const [individualDocuments, setIndividualDocuments] = useState<
    DocumentSnapshotDto[]
  >([]);
  const [deliveryEntitlements, setDeliveryEntitlements] =
    useState<DeliveryEntitlements>({
      deliveryEntitlementTotal: '-',
      contractedAmount: '-',
      availableAmount: '-',
      factorNzHolding: '-',
      factorUnion: '-',
    });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMainView, setIsMainView] = useState(true);
  const [isFullfilmentView, setIsFullfilmentView] = useState(false);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleViewAllClick = () => {
    setIsMainView(false);
  };

  const handleBackToOverview = () => {
    setIsMainView(true);
    setIsFullfilmentView(false);
  };

  const handleChangeFullfilmentView = () => {
    setIsFullfilmentView(true);
    setIsMainView(false);
  };

  useEffect(() => {
    setRecentDocuments([]);
    setIndividualDocuments([]);
    const fetchDeliveryEntitlements = async () => {
      try {
        const response = await cropConnectApiClient.getDeliveryEntitlements();
        if (response.data) {
          setDeliveryEntitlements({
            deliveryEntitlementTotal:
              response.data.deliveryEntitlementTotal ?? '-',
            contractedAmount: response.data.contractedAmount ?? '-',
            availableAmount: response.data.availableAmount ?? '-',
            factorNzHolding:
              response.data.deliveryEntitlementFactorNzHolding?.value ?? '-',
            factorUnion:
              response.data.deliveryEntitlementFactorUnion?.value ?? '-',
          });
        } else {
          setDeliveryEntitlements({
            deliveryEntitlementTotal: '-',
            contractedAmount: '-',
            availableAmount: '-',
            factorNzHolding: '-',
            factorUnion: '-',
          });
        }
      } catch (error) {
        console.error('Error fetching delivery entitlements:', error);
        setDeliveryEntitlements({
          deliveryEntitlementTotal: '-',
          contractedAmount: '-',
          availableAmount: '-',
          factorNzHolding: '-',
          factorUnion: '-',
        });
      }
    };

    // added Security Trim here to not start calling API when user is not authorized
    if (
      securityTrimConditionsMet(
        {
          requiredBusinessPartnerRoles: [
            CropConnectRoleDefinitions.Grower_DE,
            CropConnectRoleDefinitions.Grower_Organic_DE,
          ],
          onlyNordzuckerInternalUsers: false,
        },
        globalAssignment,
        businessPartnerAssignment,
        nordzuckerInternalUser
      )
    ) {
      fetchDeliveryEntitlements();
    }
    getDocumentsPage(DocumentFolder.MyContract, 3).then((d) => {
      setRecentDocuments(d?.documents || []);
    });
    getDocumentsPageWithPrefilter(
      DocumentFolder.MyBusinessInfoBeet,
      'anbauempfehlung',
      3
    ).then((d) => {
      setIndividualDocuments(d?.documents || []);
    });
  }, [activeBpNumber, getDocumentsPage, getDocumentsPageWithPrefilter]);

  return (
    <MainContainer>
      <PageHeader
        title={t(TranslationKeys.my_contracts_title)}
        extraButton={
          !isFullfilmentView && (
            <SecurityTrim
              requiredBusinessPartnerRoles={
                contractFullfilmentReportRequiredRoleDefinitions
              }
            >
              <ActionButton
                variant="accept-blue"
                buttonText={t(TranslationKeys.contract_fullfilment_title)}
                onClickAction={handleChangeFullfilmentView}
              />
            </SecurityTrim>
          )
        }
      />
      {isMainView ? (
        <MainContentWrapper>
          <CSSTransition
            in={isMainView}
            timeout={300}
            classNames="fade"
            unmountOnExit
          >
            <>
              <LeftPlaceholder>
                <SecurityTrim
                  requiredBusinessPartnerRoles={
                    contractsReportRequiredRoleDefinitions
                  }
                  fallback={
                    <div>{t(TranslationKeys.common_report_not_released)}</div>
                  }
                >
                  <ContractReport />
                </SecurityTrim>
              </LeftPlaceholder>
              <RightWidgetContainer>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <DocumentsWidget
                    documents={recentDocuments.map(mapToDocument)}
                    onViewAllClick={handleViewAllClick}
                    title={
                      t(
                        TranslationKeys.contracts_documents_widget_title
                      ) as string
                    }
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower_DE,
                    CropConnectRoleDefinitions.Grower_Organic_DE,
                  ]}
                >
                  <LieferanspruchWidget
                    countryIsoCode={mapLocaleAccordingToNzRules(
                      activeBpNumberCountry
                    )}
                    variant={'summary'}
                    data={deliveryEntitlements}
                    onMoreInfoClick={handleModalOpen}
                  />
                  <DocumentsWidget
                    documents={individualDocuments.map(mapToDocument)}
                    onViewAllClick={() =>
                      navigate(individualDocumentsAnbauempfehlungPath)
                    }
                    title={
                      t(
                        TranslationKeys.individual_documents_widget_anbauempfehlung_title
                      ) as string
                    }
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredGlobalRoles={[
                    CropConnectRoleDefinitions.Internal_Employee,
                  ]}
                >
                  <ActionButton
                    variant="accept-blue"
                    buttonText={t(
                      TranslationKeys.my_contracts_e_contracting_button
                    )}
                    onClickAction={() => {
                      navigate(eContractingPath);
                    }}
                  />
                </SecurityTrim>
                <GeneralDocumentsComponent parent="my-contracts" />
              </RightWidgetContainer>
            </>
          </CSSTransition>
        </MainContentWrapper>
      ) : (
        <DetailViewWrapper>
          <StyledActionButtonContainer>
            <ActionButton
              variant="link-button"
              buttonText={t(TranslationKeys.common_back_to_overview)}
              onClickAction={handleBackToOverview}
              back={true}
            />
          </StyledActionButtonContainer>
          {isFullfilmentView ? (
            <SecurityTrim
              requiredBusinessPartnerRoles={
                contractFullfilmentReportRequiredRoleDefinitions
              }
              fallback={
                <div>{t(TranslationKeys.common_report_not_released)}</div>
              }
            >
              <ContractFullfilmentReport />
            </SecurityTrim>
          ) : (
            <SecurityTrim
              requiredBusinessPartnerRoles={[CropConnectRoleDefinitions.Grower]}
            >
              <DocumentList
                folder={DocumentFolder.MyContract}
                title={String(t(TranslationKeys.my_contracts_title))}
                showExtraButton={false}
                hideInvoiceColumn={true}
              />
            </SecurityTrim>
          )}
        </DetailViewWrapper>
      )}
      <LieferanspruchModal
        countryIsoCode={mapLocaleAccordingToNzRules(activeBpNumberCountry)}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        data={deliveryEntitlements}
      />
    </MainContainer>
  );
};

export default MyContracts;
